import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { contratsApi, clientsApi, ContratInput } from '../services/api';
import { Contrat, Client, CONTRACT_TYPES, PAYMENT_MODES, WILAYAS } from '../types';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import AuditHistoryModal from '../components/AuditHistoryModal';
import SearchableSelect from '../components/SearchableSelect';
import { generateContratPDF } from '../utils/generateContratPDF';
import './Pages.css';

const ITEMS_PER_PAGE = 10;
const BLOCKED_CONTRACT_TYPE = 'Bloqu\u00e9';
const BLOCKABLE_CONTRACT_TYPES = new Set([
  'Instance de paiement',
  'Instance de confirmation',
]);
const SHORT_TERM_CONTRACT_TYPES = new Set([
  'Instance de confirmation',
  'Instance de paiement',
]);
const SHORT_TERM_PAYMENT_MODES = new Set([
  'Instance',
]);

const normalizeContractType = (contractType: string): string =>
  contractType === 'Expiré' ? 'Bloqué' : contractType;

const isBlockableContractType = (contractType: string): boolean =>
  BLOCKABLE_CONTRACT_TYPES.has(normalizeContractType(contractType));

const formatDateInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const calculateContractEndDate = (
  startDate: string,
  contractType: string,
  paymentMode: string
): string => {
  if (!startDate) {
    return '';
  }

  const [year, month, day] = startDate.split('-').map(Number);
  if (!year || !month || !day) {
    return '';
  }

  const endDate = new Date(year, month - 1, day);
  if (
    SHORT_TERM_CONTRACT_TYPES.has(normalizeContractType(contractType)) ||
    SHORT_TERM_PAYMENT_MODES.has(paymentMode)
  ) {
    endDate.setDate(endDate.getDate() + 30);
  } else {
    endDate.setFullYear(endDate.getFullYear() + 1);
  }

  return formatDateInput(endDate);
};

interface NewClientData {
  client_name: string;
  telephone: string;
  email: string;
  wilaya: string;
  ville: string;
}

interface RenewalDialogState {
  open: boolean;
  contrat: Contrat | null;
  modeReglt: string;
}

const RenewIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: 16, height: 16 }}>
    <path
      d="M12 4a8 8 0 1 1-7.75 10h1.58A6.5 6.5 0 1 0 12 5.5c-1.8 0-3.43.73-4.6 1.9L10 10H4V4l2.34 2.34A7.96 7.96 0 0 1 12 4Z"
      fill="currentColor"
    />
    <path d="M12 8.25a.75.75 0 0 1 .75.75v2.56l1.98 1.15a.75.75 0 0 1-.76 1.3l-2.34-1.36a.75.75 0 0 1-.38-.65V9a.75.75 0 0 1 .75-.75Z" fill="currentColor" />
  </svg>
);

const Contrats: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [contrats, setContrats] = useState<Contrat[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterMode, setFilterMode] = useState('');
  const [selectedContratIds, setSelectedContratIds] = useState<number[]>([]);
  const [bulkBlockingType, setBulkBlockingType] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { showToast, showConfirm } = useToast();
  const { hasPermission } = useAuth();

  // Audit state
  const [auditContrat, setAuditContrat] = useState<Contrat | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingContrat, setEditingContrat] = useState<Contrat | null>(null);
  const [renewingContratId, setRenewingContratId] = useState<number | null>(null);
  const [renewalDialog, setRenewalDialog] = useState<RenewalDialogState>({
    open: false,
    contrat: null,
    modeReglt: '',
  });
  const [formData, setFormData] = useState<ContratInput>({
    id_client: 0,
    date_debut: '',
    date_fin: '',
    t_contrat: '',
    mode_reglt: '',
  });

  // New client mode
  const [isNewClientMode, setIsNewClientMode] = useState(false);
  const [newClientData, setNewClientData] = useState<NewClientData>({
    client_name: '',
    telephone: '',
    email: '',
    wilaya: '',
    ville: '',
  });
  const returnToExpired = searchParams.get('return') === 'expired';

  const loadContrats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await contratsApi.getAll({
        search: search || undefined,
        type_contrat: filterType || undefined,
        mode_reglt: filterMode || undefined,
      });
      const data: Contrat[] = (Array.isArray(response.data) ? response.data : response.data?.data || response.data?.contrats || [])
        .map((contrat: Contrat) => ({
          ...contrat,
          t_contrat: normalizeContractType(contrat.t_contrat),
        }));
      setContrats(data);
      setSelectedContratIds((prev) => prev.filter((id) => data.some((contrat) => contrat.id === id)));
    } catch (err: any) {
      console.error('Erreur chargement contrats:', err?.response?.status, err?.response?.data, err?.message);
      const msg = err?.response?.status
        ? `Erreur ${err.response.status}: ${err.response.data?.message || err.message}`
        : `Erreur réseau: ${err?.message || 'Serveur inaccessible'}`;
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [search, filterType, filterMode, showToast]);

  const loadClients = useCallback(async () => {
    try {
      const response = await clientsApi.getAll();
      setClients(response.data);
    } catch (err) {
      console.error('Erreur chargement clients:', err);
    }
  }, []);

  useEffect(() => {
    loadContrats();
    loadClients();
  }, [loadContrats, loadClients]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterType, filterMode]);

  // Pagination calculations
  const totalPages = Math.ceil(contrats.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedContrats = contrats.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  const selectedContrats = contrats.filter((contrat) => selectedContratIds.includes(contrat.id));
  const selectedPaymentCount = selectedContrats.filter(
    (contrat) => normalizeContractType(contrat.t_contrat) === 'Instance de paiement'
  ).length;
  const selectedConfirmationCount = selectedContrats.filter(
    (contrat) => normalizeContractType(contrat.t_contrat) === 'Instance de confirmation'
  ).length;
  const selectablePaginatedContrats = paginatedContrats.filter((contrat) => isBlockableContractType(contrat.t_contrat));
  const allSelectableOnPageSelected =
    selectablePaginatedContrats.length > 0 &&
    selectablePaginatedContrats.every((contrat) => selectedContratIds.includes(contrat.id));
  const canBulkBlock = hasPermission('contrats', 'edit');

  // Get clients without contracts (for new contract form)
  const getAvailableClients = () => {
    const clientsWithContracts = new Set(contrats.map(c => c.id_client));
    return clients.filter(client => !clientsWithContracts.has(client.id));
  };

  const handleAdd = () => {
    setEditingContrat(null);
    const today = new Date();
    const todayStr = formatDateInput(today);
    setFormData({
      id_client: 0,
      date_debut: todayStr,
      date_fin: calculateContractEndDate(todayStr, '', ''),
      t_contrat: '',
      mode_reglt: '',
    });
    setIsNewClientMode(false);
    setNewClientData({
      client_name: '',
      telephone: '',
      email: '',
      wilaya: '',
      ville: '',
    });
    setShowModal(true);
  };

  const openEditModal = useCallback((contrat: Contrat) => {
    setEditingContrat(contrat);
    setFormData({
      id_client: contrat.id_client,
      date_debut: contrat.date_debut.split('T')[0],
      date_fin: contrat.date_fin.split('T')[0],
      t_contrat: normalizeContractType(contrat.t_contrat),
      mode_reglt: contrat.mode_reglt,
    });
    setShowModal(true);
  }, []);

  const handleEdit = (contrat: Contrat) => {
    openEditModal(contrat);
  };

  const closeModal = useCallback(() => {
    setShowModal(false);
    setEditingContrat(null);

    if (returnToExpired) {
      navigate('/expired', { replace: true });
    }
  }, [navigate, returnToExpired]);

  const openRenewalDialog = useCallback((contrat: Contrat) => {
    setRenewalDialog({
      open: true,
      contrat,
      modeReglt: contrat.mode_reglt || PAYMENT_MODES[0],
    });
  }, []);

  const closeRenewalDialog = useCallback(() => {
    if (renewingContratId !== null) {
      return;
    }

    setRenewalDialog({
      open: false,
      contrat: null,
      modeReglt: '',
    });
  }, [renewingContratId]);

  const confirmRenewal = useCallback(async () => {
    if (!renewalDialog.contrat) {
      return;
    }

    const contrat = renewalDialog.contrat;
    const newStartDate = contrat.date_fin.split('T')[0];
    const newEndDate = calculateContractEndDate(newStartDate, contrat.t_contrat, renewalDialog.modeReglt);

    try {
      setRenewingContratId(contrat.id);
      const response = await contratsApi.update(contrat.id, {
        id_client: contrat.id_client,
        date_debut: newStartDate,
        date_fin: newEndDate,
        t_contrat: contrat.t_contrat,
        mode_reglt: renewalDialog.modeReglt,
        reset_sms_counter: true,
      });
      // Client deja envoye vers Remote dont le contrat n'a pas pu y etre mis a jour
      const remoteWarning = (response.data?.remote_warning as string | undefined);
      showToast(remoteWarning || 'Contrat renouvele avec succes', remoteWarning ? 'warning' : 'success');
      setRenewalDialog({
        open: false,
        contrat: null,
        modeReglt: '',
      });
      await loadContrats();
    } catch (err) {
      showToast('Erreur lors du renouvellement du contrat', 'error');
    } finally {
      setRenewingContratId(null);
    }
  }, [loadContrats, renewalDialog, showToast]);

  useEffect(() => {
    const editParam = searchParams.get('edit');
    if (!editParam || loading || showModal) {
      return;
    }

    const contratId = Number(editParam);
    if (!contratId) {
      setSearchParams((params) => {
        const nextParams = new URLSearchParams(params);
        nextParams.delete('edit');
        return nextParams;
      }, { replace: true });
      return;
    }

    const contratToEdit = contrats.find((contrat) => contrat.id === contratId);
    if (!contratToEdit) {
      return;
    }

    openEditModal(contratToEdit);
    setSearchParams((params) => {
      const nextParams = new URLSearchParams(params);
      nextParams.delete('edit');
      return nextParams;
    }, { replace: true });
  }, [contrats, loading, openEditModal, searchParams, setSearchParams, showModal]);

  const handleDelete = (id: number) => {
    showConfirm('Êtes-vous sûr de vouloir supprimer ce contrat ?', async () => {
      try {
        const response = await contratsApi.delete(id);
        const remoteWarning = (response.data?.remote_warning as string | undefined);
        showToast(remoteWarning || 'Contrat supprimé avec succès', remoteWarning ? 'warning' : 'success');
        loadContrats();
      } catch (err) {
        showToast('Erreur lors de la suppression', 'error');
      }
    }, 'Supprimer le contrat');
  };

  const toggleContratSelection = useCallback((contratId: number) => {
    setSelectedContratIds((prev) =>
      prev.includes(contratId)
        ? prev.filter((id) => id !== contratId)
        : [...prev, contratId]
    );
  }, []);

  const togglePageSelection = useCallback(() => {
    const pageIds = paginatedContrats
      .filter((contrat) => isBlockableContractType(contrat.t_contrat))
      .map((contrat) => contrat.id);

    if (pageIds.length === 0) {
      return;
    }

    setSelectedContratIds((prev) => {
      const allSelected = pageIds.every((id) => prev.includes(id));
      if (allSelected) {
        return prev.filter((id) => !pageIds.includes(id));
      }

      return Array.from(new Set([...prev, ...pageIds]));
    });
  }, [paginatedContrats]);

  const executeBulkBlock = useCallback(async (sourceType: string, contratsToBlock: Contrat[]) => {
    try {
      setBulkBlockingType(sourceType);

      const results = await Promise.allSettled(
        contratsToBlock.map((contrat) =>
          contratsApi.update(contrat.id, {
            id_client: contrat.id_client,
            date_debut: contrat.date_debut.split('T')[0],
            date_fin: contrat.date_fin.split('T')[0],
            t_contrat: BLOCKED_CONTRACT_TYPE,
            mode_reglt: contrat.mode_reglt,
          })
        )
      );

      const successIds = contratsToBlock
        .filter((_, index) => results[index].status === 'fulfilled')
        .map((contrat) => contrat.id);
      const successCount = successIds.length;
      const failedCount = contratsToBlock.length - successCount;
      const successSuffix = successCount > 1 ? 's' : '';

      if (successCount > 0) {
        setSelectedContratIds((prev) => prev.filter((id) => !successIds.includes(id)));
        await loadContrats();
      }

      if (failedCount === 0) {
        showToast(`${successCount} contrat${successSuffix} bloque${successSuffix} avec succes`, 'success');
        return;
      }

      if (successCount > 0) {
        showToast(
          `${successCount} contrat${successSuffix} bloque${successSuffix}, ${failedCount} en echec`,
          'warning'
        );
        return;
      }

      showToast(`Aucun contrat ${sourceType.toLowerCase()} n a pu etre bloque`, 'error');
    } catch (err) {
      showToast('Erreur lors du blocage des contrats selectionnes', 'error');
    } finally {
      setBulkBlockingType(null);
    }
  }, [loadContrats, showToast]);

  const blockSelectedContrats = useCallback((sourceType: string) => {
    const contratsToBlock = selectedContrats.filter(
      (contrat) => normalizeContractType(contrat.t_contrat) === sourceType
    );

    if (contratsToBlock.length === 0) {
      showToast(`Aucun contrat selectionne de type ${sourceType}`, 'warning');
      return;
    }

    const suffix = contratsToBlock.length > 1 ? 's' : '';
    showConfirm(
      `Bloquer ${contratsToBlock.length} contrat${suffix} de type ${sourceType} ?`,
      () => { void executeBulkBlock(sourceType, contratsToBlock); },
      'Bloquer les contrats'
    );
  }, [executeBulkBlock, selectedContrats, showConfirm, showToast]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let clientId = formData.id_client;

      // If new client mode, create the client first
      if (isNewClientMode && !editingContrat) {
        if (!newClientData.client_name.trim()) {
          showToast('Le nom du client est obligatoire', 'error');
          return;
        }
        const clientResponse = await clientsApi.create(newClientData);
        clientId = clientResponse.data.id;
        // Client cree, parti de lui-meme vers Remote : il n'y est peut-etre pas arrive
        const clientWarning = (clientResponse.data?.remote_warning as string | undefined);
        showToast(clientWarning || 'Client créé avec succès', clientWarning ? 'warning' : 'success');
        loadClients(); // Refresh clients list
      }

      const contratData = { ...formData, id_client: clientId };

      if (editingContrat) {
        const response = await contratsApi.update(editingContrat.id, contratData);
        const remoteWarning = (response.data?.remote_warning as string | undefined);
        showToast(remoteWarning || 'Contrat mis à jour avec succès', remoteWarning ? 'warning' : 'success');
      } else {
        if (clientId === 0) {
          showToast('Veuillez sélectionner ou créer un client', 'error');
          return;
        }
        const response = await contratsApi.create(contratData);
        const remoteWarning = (response.data?.remote_warning as string | undefined);
        showToast(remoteWarning || 'Contrat créé avec succès', remoteWarning ? 'warning' : 'success');
      }
      if (returnToExpired && editingContrat) {
        navigate('/expired', { replace: true });
      } else {
        setShowModal(false);
        setEditingContrat(null);
        loadContrats();
      }
    } catch (err) {
      showToast('Erreur lors de l\'enregistrement', 'error');
    }
  }, [editingContrat, formData, isNewClientMode, loadClients, loadContrats, navigate, newClientData, returnToExpired, showToast]);

  const handleDownloadPDF = (contrat: Contrat) => {
    const client = clients.find(c => c.id === contrat.id_client);
    generateContratPDF({
      ...contrat,
      telephone: client?.telephone,
      email: client?.email,
      wilaya: client?.wilaya,
      ville: client?.ville,
    });
  };

  const isExpiringSoon = (dateFin: string) => {
    const endDate = new Date(dateFin);
    const today = new Date();
    const diffDays = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays <= 10 && diffDays >= 0;
  };

  const isExpired = (dateFin: string) => {
    return new Date(dateFin) < new Date();
  };

  const renewalStartDate = renewalDialog.contrat ? renewalDialog.contrat.date_fin.split('T')[0] : '';
  const renewalEndDate = renewalDialog.contrat
    ? calculateContractEndDate(renewalStartDate, renewalDialog.contrat.t_contrat, renewalDialog.modeReglt)
    : '';

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Gestion des Contrats</h1>
        {(canBulkBlock || hasPermission('contrats', 'create')) && (
          <div className="header-actions">
            {canBulkBlock && (
              <>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={() => blockSelectedContrats('Instance de paiement')}
                  disabled={selectedPaymentCount === 0 || bulkBlockingType !== null}
                >
                  {bulkBlockingType === 'Instance de paiement'
                    ? 'Blocage paiement...'
                    : `Bloquer paiement (${selectedPaymentCount})`}
                </button>
                <button
                  type="button"
                  className="btn btn-warning-alt"
                  onClick={() => blockSelectedContrats('Instance de confirmation')}
                  disabled={selectedConfirmationCount === 0 || bulkBlockingType !== null}
                >
                  {bulkBlockingType === 'Instance de confirmation'
                    ? 'Blocage confirmation...'
                    : `Bloquer confirmation (${selectedConfirmationCount})`}
                </button>
              </>
            )}
            {hasPermission('contrats', 'create') && (
              <button className="btn btn-primary" onClick={handleAdd}>
                + Nouveau Contrat
              </button>
            )}
          </div>
        )}
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="Rechercher par client ou téléphone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="">Tous les types</option>
          {CONTRACT_TYPES.map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
        <select
          value={filterMode}
          onChange={(e) => setFilterMode(e.target.value)}
        >
          <option value="">Tous les modes</option>
          {PAYMENT_MODES.map((mode) => (
            <option key={mode} value={mode}>{mode}</option>
          ))}
        </select>
      </div>

      {canBulkBlock && (
        <div className="bulk-selection-bar">
          <span className="bulk-selection-info">
            {selectedContratIds.length} contrat{selectedContratIds.length > 1 ? 's' : ''} selectionne{selectedContratIds.length > 1 ? 's' : ''}
          </span>
          <span className="bulk-selection-note">
            Selection autorisee uniquement pour les types Instance de paiement et Instance de confirmation.
          </span>
        </div>
      )}

      {loading ? (
        <div className="loading">Chargement...</div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                {canBulkBlock && (
                  <th className="checkbox-cell">
                    <input
                      type="checkbox"
                      checked={allSelectableOnPageSelected}
                      onChange={togglePageSelection}
                      disabled={selectablePaginatedContrats.length === 0 || bulkBlockingType !== null}
                      aria-label="Selectionner les contrats de la page"
                    />
                  </th>
                )}
                <th>Client</th>
                <th>Téléphone</th>
                <th>Date début</th>
                <th>Date fin</th>
                <th>Type</th>
                <th>Mode règlement</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedContrats.length === 0 ? (
                <tr>
                  <td colSpan={canBulkBlock ? 8 : 7} style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    {search || filterType || filterMode ? 'Aucun contrat trouvé pour ces filtres' : 'Aucun contrat enregistré'}
                  </td>
                </tr>
              ) : (
                paginatedContrats.map((contrat) => (
                  <tr
                    key={contrat.id}
                    className={
                      isExpired(contrat.date_fin)
                        ? 'row-expired'
                        : isExpiringSoon(contrat.date_fin)
                        ? 'row-warning'
                        : ''
                    }
                  >
                    {canBulkBlock && (
                      <td className="checkbox-cell">
                        <input
                          type="checkbox"
                          checked={selectedContratIds.includes(contrat.id)}
                          onChange={() => toggleContratSelection(contrat.id)}
                          disabled={!isBlockableContractType(contrat.t_contrat) || bulkBlockingType !== null}
                          title={isBlockableContractType(contrat.t_contrat)
                            ? 'Selectionner ce contrat'
                            : 'Seuls les contrats Instance de paiement ou Instance de confirmation peuvent etre bloques ici'}
                          aria-label={`Selectionner ${contrat.client_name}`}
                        />
                      </td>
                    )}
                    <td>{contrat.client_name}</td>
                    <td>{(contrat as any).telephone || '-'}</td>
                    <td>{new Date(contrat.date_debut).toLocaleDateString()}</td>
                    <td>{new Date(contrat.date_fin).toLocaleDateString()}</td>
                    <td>{contrat.t_contrat}</td>
                    <td>{contrat.mode_reglt}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon"
                          onClick={() => handleDownloadPDF(contrat)}
                          title="Télécharger le contrat PDF"
                        >
                          📄
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => setAuditContrat(contrat)}
                          title="Historique"
                        >
                          🕐
                        </button>
                        {hasPermission('contrats', 'edit') && (
                          <button
                            className="btn-icon"
                            onClick={() => openRenewalDialog(contrat)}
                            title="Renouveler"
                            disabled={renewingContratId === contrat.id}
                          >
                            <RenewIcon />
                          </button>
                        )}
                        {hasPermission('contrats', 'edit') && (
                          <button
                            className="btn-icon"
                            onClick={() => handleEdit(contrat)}
                            title="Modifier"
                          >
                            ✏️
                          </button>
                        )}
                        {hasPermission('contrats', 'delete') && (
                          <button
                            className="btn-icon"
                            onClick={() => handleDelete(contrat.id)}
                            title="Supprimer"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && contrats.length > ITEMS_PER_PAGE && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            ««
          </button>
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            «
          </button>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={currentPage === pageNum ? 'active' : ''}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            »
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            »»
          </button>

          <span className="pagination-info">
            {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, contrats.length)} sur {contrats.length}
          </span>
        </div>
      )}

      {auditContrat && (
        <AuditHistoryModal
          tableName="contrats"
          recordId={auditContrat.id}
          recordLabel={auditContrat.client_name}
          fieldLabels={{
            id_client: 'Client',
            date_debut: 'Date début',
            date_fin: 'Date fin',
            t_contrat: 'Type contrat',
            mode_reglt: 'Mode règlement',
          }}
          onClose={() => setAuditContrat(null)}
        />
      )}

      {renewalDialog.open && renewalDialog.contrat && (
        <div className="modal-overlay" onClick={closeRenewalDialog}>
          <div className="modal" onClick={(event) => event.stopPropagation()}>
            <h2>Renouveler le contrat</h2>
            <div className="form-group">
              <label>Client</label>
              <input type="text" value={renewalDialog.contrat.client_name} disabled />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Type de contrat</label>
                <input type="text" value={renewalDialog.contrat.t_contrat} disabled />
              </div>
              <div className="form-group">
                <label>Mode de reglement</label>
                <select
                  value={renewalDialog.modeReglt}
                  onChange={(event) => setRenewalDialog((prev) => ({
                    ...prev,
                    modeReglt: event.target.value,
                  }))}
                >
                  {PAYMENT_MODES.map((mode) => (
                    <option key={mode} value={mode}>{mode}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Nouvelle date debut</label>
                <input type="date" value={renewalStartDate} disabled />
              </div>
              <div className="form-group">
                <label>Nouvelle date fin</label>
                <input type="date" value={renewalEndDate} disabled />
              </div>
            </div>

            <p className="info-message">
              Le renouvellement reprend l ancienne date fin comme nouvelle date debut et recalcule la date fin selon le type de contrat et le mode de reglement choisi.
            </p>

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={closeRenewalDialog}>
                Annuler
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => { void confirmRenewal(); }}
                disabled={renewingContratId === renewalDialog.contrat.id}
              >
                {renewingContratId === renewalDialog.contrat.id ? 'Renouvellement...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{editingContrat ? 'Modifier le contrat' : 'Nouveau contrat'}</h2>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
              <div className="form-group" style={{ order: -2 }}>
                <div className="client-selection-header">
                  <label>Client</label>
                  {!editingContrat && (
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => {
                        setIsNewClientMode(!isNewClientMode);
                        if (!isNewClientMode) {
                          setFormData({ ...formData, id_client: 0 });
                        }
                      }}
                    >
                      {isNewClientMode ? '← Sélectionner un client existant' : '+ Nouveau client'}
                    </button>
                  )}
                </div>

                {!isNewClientMode || editingContrat ? (
                  <>
                    <SearchableSelect
                      options={(editingContrat ? clients : getAvailableClients()).map((c) => ({
                        value: c.id,
                        label: c.client_name,
                      }))}
                      value={formData.id_client}
                      onChange={(val) => setFormData({ ...formData, id_client: val })}
                      placeholder="Saisir le nom du client..."
                      required={!isNewClientMode}
                    />
                    {!editingContrat && getAvailableClients().length === 0 && (
                      <p className="info-message">Tous les clients ont déjà un contrat. Créez un nouveau client.</p>
                    )}
                  </>
                ) : (
                  <div className="new-client-fields">
                    <input
                      type="text"
                      placeholder="Nom du client *"
                      value={newClientData.client_name}
                      onChange={(e) => setNewClientData({ ...newClientData, client_name: e.target.value })}
                      required
                    />
                    <div className="form-row">
                      <input
                        type="tel"
                        placeholder="Téléphone"
                        value={newClientData.telephone}
                        onChange={(e) => setNewClientData({ ...newClientData, telephone: e.target.value })}
                      />
                      <input
                        type="email"
                        placeholder="Email (optionnel)"
                        value={newClientData.email}
                        onChange={(e) => setNewClientData({ ...newClientData, email: e.target.value })}
                      />
                    </div>
                    <div className="form-row">
                      <select
                        value={newClientData.wilaya}
                        onChange={(e) => setNewClientData({ ...newClientData, wilaya: e.target.value })}
                      >
                        <option value="">Wilaya</option>
                        {WILAYAS.map((w) => (
                          <option key={w} value={w}>{w}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        placeholder="Ville"
                        value={newClientData.ville}
                        onChange={(e) => setNewClientData({ ...newClientData, ville: e.target.value })}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date début</label>
                  <input
                    type="date"
                    value={formData.date_debut}
                    onChange={(e) => {
                      const dateDebut = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        date_debut: dateDebut,
                        date_fin: calculateContractEndDate(dateDebut, prev.t_contrat, prev.mode_reglt),
                      }));
                    }}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Date fin</label>
                  <input
                    type="date"
                    value={formData.date_fin}
                    onChange={(e) => setFormData({ ...formData, date_fin: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-row" style={{ order: -1 }}>
                <div className="form-group">
                  <label>Type de contrat</label>
                  <select
                    value={formData.t_contrat}
                    onChange={(e) => {
                      const contractType = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        t_contrat: contractType,
                        date_fin: calculateContractEndDate(prev.date_debut, contractType, prev.mode_reglt),
                      }));
                    }}
                    required
                  >
                    <option value="">Sélectionner</option>
                    {CONTRACT_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Mode de règlement</label>
                  <select
                    value={formData.mode_reglt}
                    onChange={(e) => {
                      const paymentMode = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        mode_reglt: paymentMode,
                        date_fin: calculateContractEndDate(prev.date_debut, prev.t_contrat, paymentMode),
                      }));
                    }}
                    required
                  >
                    <option value="">Sélectionner</option>
                    {PAYMENT_MODES.map((mode) => (
                      <option key={mode} value={mode}>{mode}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingContrat ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contrats;
